import { useEffect, useRef } from 'react';
import { TreatmentFormSnapshot } from './treatmentFormValues';

interface UnconfirmedTreatmentFormOptions {
  isModalOpen: boolean;
  /** The edited treatment; the modal's open-reset runs again when it changes. */
  treatment: unknown;
  singleReminderOnly: boolean;
  /** While the host's write series is unconfirmed: the frozen request's snapshot. */
  snapshot: TreatmentFormSnapshot | undefined;
  /** Shows the snapshot: the fields, the lines and the catalogue pick. */
  restore: (snapshot: TreatmentFormSnapshot) => void;
}

/**
 * R3-4: a treatment form reopened while its host's write series is
 * unconfirmed shows what its submit will resend, not the saved treatment
 * (Task 24 concern 1, like `MedicationFormModal`). Call it right after the
 * modal's open-reset effect: it runs on the same triggers, and effects run in
 * order, so the snapshot lands on that reset. The snapshot is read when the
 * form (re)opens, not whenever it changes: a failed attempt only freezes
 * what the open form already shows.
 */
export function useUnconfirmedTreatmentForm({
  isModalOpen,
  treatment,
  singleReminderOnly,
  snapshot,
  restore,
}: UnconfirmedTreatmentFormOptions): void {
  const latest = useRef({ snapshot, restore });
  latest.current = { snapshot, restore };

  useEffect(() => {
    const { snapshot: frozen, restore: show } = latest.current;
    if (isModalOpen && frozen) show(frozen);
  }, [isModalOpen, treatment, singleReminderOnly]);
}
